import { type HTMLAttributes } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

export type SpinnerSize = 'sm' | 'md' | 'lg' | 'xl';
export type SpinnerVariant = 'primary' | 'accent' | 'white' | 'gray';

export interface LoadingSpinnerProps extends HTMLAttributes<HTMLDivElement> {
  /** Spinner size */
  size?: SpinnerSize;
  /** Color variant */
  variant?: SpinnerVariant;
  /** Loading message for screen readers */
  label?: string;
  /** Show text label below spinner */
  showLabel?: boolean;
  /** Center the spinner in container */
  centered?: boolean;
  /** Full screen overlay */
  fullScreen?: boolean;
}

const sizeClasses: Record<SpinnerSize, { spinner: string; border: string }> = {
  sm: { spinner: 'h-4 w-4', border: 'border-2' },
  md: { spinner: 'h-8 w-8', border: 'border-3' },
  lg: { spinner: 'h-12 w-12', border: 'border-4' },
  xl: { spinner: 'h-16 w-16', border: 'border-4' },
};

const variantClasses: Record<SpinnerVariant, { track: string; indicator: string }> = {
  primary: {
    track: 'border-primary-blue/20',
    indicator: 'border-primary-blue',
  },
  accent: {
    track: 'border-accent-teal/20',
    indicator: 'border-accent-teal',
  },
  white: {
    track: 'border-white/30',
    indicator: 'border-white',
  },
  gray: {
    track: 'border-gray-300',
    indicator: 'border-warm-gray',
  },
};

/**
 * LoadingSpinner Component
 *
 * An animated loading indicator with multiple sizes and color variants.
 * Fully accessible with ARIA attributes for screen readers.
 *
 * @example
 * ```tsx
 * // Simple spinner
 * <LoadingSpinner />
 *
 * // With label
 * <LoadingSpinner size="lg" label="Loading content..." showLabel />
 *
 * // Full screen overlay
 * <LoadingSpinner fullScreen variant="primary" />
 * ```
 */
export function LoadingSpinner({
  size = 'md',
  variant = 'primary',
  label = 'Loading',
  showLabel = false,
  centered = false,
  fullScreen = false,
  className,
  ...props
}: LoadingSpinnerProps) {
  const { spinner: spinnerSize, border: borderWidth } = sizeClasses[size];
  const { track, indicator } = variantClasses[variant];

  const spinnerContent = (
    <div
      className={cn(
        centered && 'flex flex-col items-center justify-center',
        className
      )}
      {...props}
    >
      <div className="relative" role="status" aria-label={label}>
        {/* Track (background circle) */}
        <div
          className={cn(
            'rounded-full',
            spinnerSize,
            borderWidth,
            track
          )}
          aria-hidden="true"
        />

        {/* Animated indicator */}
        <motion.div
          className={cn(
            'absolute top-0 left-0 rounded-full',
            spinnerSize,
            borderWidth,
            'border-t-transparent border-r-transparent',
            indicator
          )}
          animate={{ rotate: 360 }}
          transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'linear',
          }}
          aria-hidden="true"
        />

        {/* Screen reader text */}
        <span className="sr-only">{label}</span>
      </div>

      {showLabel && (
        <p
          className={cn(
            'mt-3 text-center',
            variant === 'white' ? 'text-white' : 'text-warm-gray',
            size === 'sm' && 'text-caption',
            size === 'md' && 'text-body-sm',
            (size === 'lg' || size === 'xl') && 'text-body'
          )}
          aria-hidden="true"
        >
          {label}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm"
        aria-live="assertive"
        aria-busy="true"
      >
        {spinnerContent}
      </div>
    );
  }

  return spinnerContent;
}

export default LoadingSpinner;
